/**
 * Runtime configuration loader.
 *
 * The website stack writes a `settings.json` at the CloudFront distribution
 * root containing the AppSync endpoint + Cognito ids (no values are hard-coded
 * in this package). At startup the app fetches that file and caches it.
 *
 * When `settings.json` is absent (local dev / unit tests), the loader returns
 * null and callers fall back to local mock behavior.
 *
 * Auth + GraphQL are done with plain fetch (no aws-amplify), avoiding the heavy
 * AWS SDK dependency tree.
 */
export interface RuntimeSettings {
  region: string;
  graphqlEndpoint: string;
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId: string;
  cognitoDomain: string;
}

let cached: RuntimeSettings | null = null;
let loaded = false;

export function getSettings(): RuntimeSettings | null {
  return cached;
}

/**
 * Fetch settings.json (once) and cache it. Returns null if absent/invalid.
 */
export async function loadRuntimeConfig(): Promise<RuntimeSettings | null> {
  if (loaded) return cached;
  loaded = true;

  try {
    const res = await fetch("/settings.json", { cache: "no-store" });
    if (!res.ok) return null;
    const s = (await res.json()) as RuntimeSettings;
    if (!s.graphqlEndpoint || !s.userPoolClientId || !s.cognitoDomain)
      return null;
    cached = s;
    return s;
  } catch (e) {
    // Network/parse error — treat as "not configured" so the UI still loads.
    // eslint-disable-next-line no-console
    console.warn("Runtime settings unavailable; using local mock mode.", e);
    return null;
  }
}
